import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";

/**
 * Editorial marquee — slow horizontal ticker of service words.
 * Scroll-linked drift adds a slow-motion cinematic feel.
 */
export default function Marquee() {
  const { t } = useTranslation();
  const words = t("marquee.words", { returnObjects: true }) || [];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -220]);

  // Duplicate words for seamless loop
  const loop = [...words, ...words, ...words];

  return (
    <section
      ref={ref}
      data-testid="marquee-section"
      className="relative py-14 md:py-20 bg-[#0A1128] overflow-hidden border-y border-white/5"
    >
      {/* faint diagonal texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, #FF5A1F 0, #FF5A1F 1px, transparent 1px, transparent 32px)",
        }}
      />
      <motion.div
        className="flex whitespace-nowrap will-change-transform"
        style={{ x }}
        aria-hidden
      >
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
        >
          {loop.map((w, i) => (
            <span
              key={i}
              className="font-display text-6xl md:text-8xl lg:text-9xl leading-none px-8 md:px-12 text-white/90"
            >
              {w}
              <span className="inline-block w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#FF5A1F] mx-8 md:mx-12 align-middle" />
            </span>
          ))}
        </motion.div>
      </motion.div>

      {/* Subtitle strip */}
      <div className="relative mt-8 md:mt-10 flex items-center justify-center gap-4 px-5">
        <span className="w-10 h-px bg-[#FF5A1F]" />
        <span className="text-white/60 uppercase font-bold tracking-[0.35em] text-[10px] md:text-[11px] text-center">
          Ataiwo · Elektrotechniek · Amsterdam · Est. 2014
        </span>
        <span className="w-10 h-px bg-[#FF5A1F]" />
      </div>
    </section>
  );
}
